// =============================================================
// VARIANCE — The Executive Command Center
// File      : AlignmentChart.tsx
// Location  : /home/Applications/variance/web/src/components/AlignmentChart.tsx
// Owner     : VARIANCE frontend application only
// Purpose   : Cause & Effect (Alignment) Charts comparing 
//             Construction (Execution) vs Revenue (Sales).
// =============================================================

import ReactECharts from 'echarts-for-react';

interface Milestone {
  milestone_type: string;
  planned_amount: number | null;
  actual_amount: number | null;
  weightage_pct: number;
  physical_progress_pct: number;
}

export function AlignmentChart({ milestones }: { milestones: Milestone[] }) {
  // --- DATA CALCULATIONS ---
  const constructionMilestones = milestones.filter(m => m.milestone_type === 'Construction Milestone');
  const revenueMilestones = milestones.filter(m => m.milestone_type === 'Revenue Milestone');

  const plannedConstruction = constructionMilestones.reduce((sum, m) => sum + (m.planned_amount || 0), 0);
  const actualConstruction = constructionMilestones.reduce((sum, m) => sum + (m.actual_amount || 0), 0);
  const plannedRevenue = revenueMilestones.reduce((sum, m) => sum + (m.planned_amount || 0), 0);
  const actualRevenue = revenueMilestones.reduce((sum, m) => sum + (m.actual_amount || 0), 0);

  // Progress Calculations
  // Construction Progress is weighted
  const constructionProgress = constructionMilestones.length > 0
    ? constructionMilestones.reduce((sum, m) => sum + (m.physical_progress_pct * (m.weightage_pct / 100)), 0)
    : 0;
    
  // Revenue Progress is (Actual / Planned) * 100
  const revenueProgress = plannedRevenue > 0
    ? (actualRevenue / plannedRevenue) * 100
    : 0;

  // --- CHART OPTIONS ---
  const financialOption = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: '#1A2540',
      borderColor: '#22304D',
      textStyle: { color: '#F1F5FB' },
      valueFormatter: (value: number) => `₹${value.toLocaleString('en-IN')}`
    },
    legend: {
      data: ['Planned', 'Actual'],
      textStyle: { color: '#94A3B8' },
      bottom: 0,
    },
    grid: { left: '3%', right: '4%', bottom: '15%', top: '10%', containLabel: true },
    xAxis: {
      type: 'category',
      data: ['Construction (Execution)', 'Revenue (Sales)'],
      axisLabel: { color: '#94A3B8' },
      axisLine: { lineStyle: { color: '#22304D' } }
    },
    yAxis: {
      type: 'value',
      axisLabel: { 
        color: '#94A3B8',
        formatter: (value: number) => value >= 10000000 ? `₹${(value / 10000000).toFixed(1)}Cr` : `₹${(value / 100000).toFixed(1)}L`
      },
      splitLine: { lineStyle: { color: '#22304D', type: 'dashed' } }
    },
    series: [
      {
        name: 'Planned',
        type: 'bar',
        barWidth: '25%',
        itemStyle: { color: '#3B82F6', borderRadius: [4, 4, 0, 0] },
        data: [plannedConstruction, plannedRevenue]
      },
      {
        name: 'Actual',
        type: 'bar',
        barWidth: '25%',
        itemStyle: { color: '#F97316', borderRadius: [4, 4, 0, 0] },
        data: [actualConstruction, actualRevenue]
      }
    ]
  };

  const progressOption = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: '#1A2540',
      borderColor: '#22304D',
      textStyle: { color: '#F1F5FB' },
      valueFormatter: (value: number) => `${value.toFixed(1)}%`
    },
    legend: {
      data: ['Completion %'],
      textStyle: { color: '#94A3B8' },
      bottom: 0,
    },
    grid: { left: '3%', right: '4%', bottom: '15%', top: '10%', containLabel: true },
    xAxis: {
      type: 'category',
      data: ['Construction Progress', 'Revenue Collected'],
      axisLabel: { color: '#94A3B8' },
      axisLine: { lineStyle: { color: '#22304D' } }
    },
    yAxis: {
      type: 'value',
      max: 100,
      axisLabel: { color: '#94A3B8', formatter: '{value}%' },
      splitLine: { lineStyle: { color: '#22304D', type: 'dashed' } }
    },
    series: [
      {
        name: 'Completion %',
        type: 'bar',
        barWidth: '25%',
        itemStyle: { 
          color: (params: any) => params.dataIndex === 0 ? '#8B5CF6' : '#22C55E', // Purple for Construction, Green for Revenue
          borderRadius: [4, 4, 0, 0] 
        },
        data: [constructionProgress, revenueProgress]
      }
    ]
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Financial Chart */}
      <div className="p-6 rounded-xl h-full flex flex-col" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)' }}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
            Financial Alignment
          </h2>
          <span className="text-xs font-medium px-2 py-1 rounded bg-[var(--bg-panel-alt)]" style={{ color: 'var(--text-secondary)' }}>
            Plan vs Actual
          </span>
        </div>
        <div className="flex-1 min-h-[300px]">
          <ReactECharts option={financialOption} style={{ height: '100%', width: '100%' }} />
        </div>
      </div>

      {/* Progress Chart */}
      <div className="p-6 rounded-xl h-full flex flex-col" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)' }}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
            Cause & Effect (Progress)
          </h2>
          <span className="text-xs font-medium px-2 py-1 rounded bg-[var(--bg-panel-alt)]" style={{ color: 'var(--text-secondary)' }}>
            Execution vs Collection
          </span>
        </div>
        <div className="flex-1 min-h-[300px]">
          <ReactECharts option={progressOption} style={{ height: '100%', width: '100%' }} />
        </div>
      </div>
    </div>
  );
}
